import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../api";
import BroadcastForm from "../components/BroadcastForm";

/**
 * BroadcastPage
 * - Shows list of broadcasts
 * - Lets admin create a broadcast (BroadcastForm)
 * - When a broadcast is selected, shows simple stats and polls status
 */
export default function BroadcastPage() {
  const [searchParams] = useSearchParams();
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [showFailedList, setShowFailedList] = useState(false);
  const [error, setError] = useState("");
  const pollRef = useRef(null);

  // Auto-select broadcast from query param (e.g. /broadcasts?id=xxx)
  useEffect(() => {
    const idFromUrl = searchParams.get('id');
    if (idFromUrl && !selected) {
      setSelected(idFromUrl);
    }
  }, [searchParams]);

  const fetchBroadcasts = async () => {
    try {
      setError("");
      setLoading(true);
      const res = await api.get("/broadcasts");
      // Handle both paginated object and simple array for robustness
      if (res.data && res.data.data) {
        setBroadcasts(res.data.data);
      } else {
        setBroadcasts(res.data || []);
      }
    } catch (err) {
      setError("Failed to load broadcasts");
    } finally {
      setLoading(false);
    }
  };

  const fetchDetails = useCallback(async (id) => {
    if (!id) {
      setDetails(null);
      setRecipients([]);
      return;
    }

    try {
      setError("");
      const res = await api.get(`/broadcasts/${id}`);

      // Check if the response has the expected structure
      if (res.data && res.data.broadcast) {
        // Merge stats into the broadcast object so UI can use details.stats
        setDetails({
          ...res.data.broadcast,
          stats: res.data.stats
        });
        setRecipients(res.data.recipients || []);
      } else if (res.data) {
        setDetails(res.data);
        setRecipients([]);
      } else {
        setDetails(null);
        setRecipients([]);
      }

    } catch (err) {
      setError('Failed to load broadcast details');
      setDetails(null);
      setRecipients([]);
    }
  }, []);

  // Poll selected broadcast status every 5s
  useEffect(() => {
    if (!selected) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    // initial fetch
    fetchDetails(selected);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => fetchDetails(selected), 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [selected, fetchDetails]);

  useEffect(() => {
    fetchBroadcasts();
    // refresh list periodically (once per 30s) to show new broadcasts
    const interval = setInterval(fetchBroadcasts, 30000);
    return () => clearInterval(interval);
  }, []);

  // Handler after a new broadcast is created by BroadcastForm.
  // BroadcastForm currently posts to /api/broadcasts/create; it returns broadcastId & total.
  // We poll broadcast list to refresh UI, but allow immediate optional fetch.
  const onBroadcastCreated = useCallback(async (resObj) => {
    // resObj may be the API response. We'll refresh list and optionally select the new broadcast
    await fetchBroadcasts();
    if (resObj && resObj.broadcastId) {
      setSelected(resObj.broadcastId);
      // short delay to allow backend to create records
      setTimeout(() => fetchDetails(resObj.broadcastId), 1000);
    }
  }, []);

  // Expose callback globally so BroadcastForm can call it
  useEffect(() => {
    window.onBroadcastCreated = onBroadcastCreated;
    return () => {
      delete window.onBroadcastCreated;
    };
  }, [onBroadcastCreated]);

  return (
    <div className="yc-broadcast">
      <aside className="yc-bc-left">
        <div className="yc-bc-create">
          <h2 className="yc-bc-title">Create Broadcast</h2>
          <BroadcastForm onCreated={onBroadcastCreated} />
        </div>
        <div className="yc-bc-list" style={{ padding: '16px', textAlign: 'center' }}>
          <button
            onClick={() => window.location.href = '/broadcast-history'}
            className="yc-send-btn"
            style={{ width: '100%' }}
          >
            📋 View Broadcast History
          </button>
        </div>
      </aside>
      <section className="yc-bc-right">
        <div className="yc-bc-detail">
          <h3 className="yc-bc-title">Broadcast Details</h3>
          {!selected ? (
            <div className="yc-chat-empty">Select a broadcast to view details.</div>
          ) : !details ? (
            <div className="yc-chat-empty">Loading details...</div>
          ) : (
            <>
              <div className="yc-bc-detail-grid">
                <div className="yc-bc-detail-item">
                  <span className="yc-bc-detail-label">Name:</span>
                  <span className="yc-bc-detail-value">{details.name || 'N/A'}</span>
                </div>
                <div className="yc-bc-detail-item">
                  <span className="yc-bc-detail-label">Template:</span>
                  <span className="yc-bc-detail-value">{details.templateName || 'N/A'} ({details.templateLanguage || 'N/A'})</span>
                </div>
                <div className="yc-bc-detail-item">
                  <span className="yc-bc-detail-label">Sender Phone ID:</span>
                  <span className="yc-bc-detail-value">{details.senderPhoneId || 'N/A'}</span>
                </div>
                <div className="yc-bc-detail-item">
                  <span className="yc-bc-detail-label">Total Recipients:</span>
                  <span className="yc-bc-detail-value">{details.totalRecipients ?? "N/A"}</span>
                </div>
                <div className="yc-bc-detail-item">
                  <span className="yc-bc-detail-label">Sent:</span>
                  <span className="yc-bc-detail-value text-blue-600 font-bold">{details.stats?.sent ?? 0}</span>
                </div>
                <div className="yc-bc-detail-item">
                  <span className="yc-bc-detail-label">Delivered:</span>
                  <span className="yc-bc-detail-value text-green-600 font-bold">{details.stats?.delivered ?? 0}</span>
                </div>
                <div className="yc-bc-detail-item">
                  <span className="yc-bc-detail-label">Failed:</span>
                  <span className="yc-bc-detail-value text-red-600 font-bold">{details.stats?.failed ?? 0}</span>
                </div>
                <div className="yc-bc-detail-item">
                  <span className="yc-bc-detail-label">Status:</span>
                  <span className="yc-bc-detail-value" style={{ textTransform: 'capitalize' }}>{details.status || 'N/A'}</span>
                </div>
                <div className="yc-bc-detail-item">
                  <span className="yc-bc-detail-label">Created:</span>
                  <span className="yc-bc-detail-value">{details.createdAt ? new Date(details.createdAt).toLocaleString() : 'N/A'}</span>
                </div>
              </div>

              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '12px', marginBottom: '4px', color: '#666' }}>Progress</div>
                <div style={{ width: '100%', backgroundColor: '#e0e0e0', borderRadius: '4px', height: '16px' }}>
                  <div
                    style={{
                      height: '16px',
                      borderRadius: '4px',
                      backgroundColor: '#2196f3',
                      width: `${Math.min(
                        100,
                        ((details.sentCount || 0) / Math.max(1, details.totalRecipients || 1)) * 100
                      )}%`,
                      transition: 'width 0.3s ease'
                    }}
                  />
                </div>
                <div style={{ fontSize: '12px', marginTop: '4px', color: '#666' }}>
                  {details.sentCount ?? 0} / {details.totalRecipients ?? 0} sent
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button
                  onClick={() => fetchDetails(selected)}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '12px',
                    backgroundColor: '#8bc34a',
                    color: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  Refresh
                </button>

                {(details.stats?.failed > 0 || details.status === 'failed') && (
                  <button
                    onClick={async () => {
                      if (!confirm(`Retry sending to ${details.stats?.failed || 'failed'} recipients?`)) return;
                      try {
                        await api.post(`/broadcasts/${selected}/retry`);
                        await fetchDetails(selected);
                        await fetchBroadcasts();
                      } catch (err) {
                        console.error("Retry failed", err);
                        alert("Retry failed. Check server logs.");
                      }
                    }}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: '#ff9800',
                      color: '#fff',
                      cursor: 'pointer'
                    }}
                  >
                    Retry Failed
                  </button>
                )}

                {details.status === 'running' || details.status === 'pending' ? (
                  <button
                    onClick={async () => {
                      if (!confirm("Cancel this broadcast? This will stop queued sends.")) return;
                      try {
                        await api.post(`/broadcasts/${selected}/cancel`);
                        await fetchDetails(selected);
                        await fetchBroadcasts();
                      } catch (err) {
                        console.error("Cancel failed", err);
                        alert("Cancel failed. Check server logs.");
                      }
                    }}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: '#fff',
                      color: '#f44336',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                ) : details.status === 'paused' ? (
                  <button
                    onClick={async () => {
                      if (!confirm("Resume this broadcast? This will restart queued sends.")) return;
                      try {
                        await api.post(`/broadcasts/${selected}/resume`);
                        await fetchDetails(selected);
                        await fetchBroadcasts();
                      } catch (err) {
                        console.error("Resume failed", err);
                        alert("Resume failed. Check server logs.");
                      }
                    }}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: '#fff',
                      color: '#4caf50',
                      cursor: 'pointer'
                    }}
                  >
                    Resume
                  </button>
                ) : null}
              </div>

              {/* Participants Section */}
              <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #ddd' }}>
                <div style={{ display: 'flex', borderBottom: '1px solid #ddd', marginBottom: '12px' }}>
                  <button
                    onClick={() => setShowFailedList(false)}
                    style={{
                      padding: '10px 20px',
                      border: 'none',
                      backgroundColor: !showFailedList ? '#fff' : 'transparent',
                      borderBottom: !showFailedList ? '2px solid #2196f3' : 'none',
                      color: !showFailedList ? '#2196f3' : '#666',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      transition: 'all 0.2s'
                    }}
                  >
                    ✓ Sent ({recipients.filter(r => r.status === 'sent' || r.status === 'delivered').length})
                  </button>
                  <button
                    onClick={() => setShowFailedList(true)}
                    style={{
                      padding: '10px 20px',
                      border: 'none',
                      backgroundColor: showFailedList ? '#fff' : 'transparent',
                      borderBottom: showFailedList ? '2px solid #f44336' : 'none',
                      color: showFailedList ? '#f44336' : '#666',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      transition: 'all 0.2s'
                    }}
                  >
                    ✕ Failed ({details.stats?.failed || 0})
                  </button>
                </div>

                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#eeeeee', borderBottom: '1px solid #ddd' }}>
                        <th style={{ padding: '10px', textAlign: 'left' }}>Name</th>
                        <th style={{ padding: '10px', textAlign: 'left' }}>Phone</th>
                        {showFailedList && <th style={{ padding: '10px', textAlign: 'left' }}>Error</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {recipients
                        .filter(r => showFailedList ? r.status === 'failed' : (r.status === 'sent' || r.status === 'delivered'))
                        .map((r, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '10px' }}>
                              {r.contactId?.name || 'Unknown'}
                            </td>
                            <td style={{ padding: '10px' }}>
                              {r.contactId?.phone || r.phone || 'N/A'}
                            </td>
                            {showFailedList && (
                              <td style={{ padding: '10px', fontSize: '12px', color: '#d84315' }}>
                                {r.error || 'N/A'}
                              </td>
                            )}
                          </tr>
                        ))}
                      {recipients.filter(r => showFailedList ? r.status === 'failed' : (r.status === 'sent' || r.status === 'delivered')).length === 0 && (
                        <tr>
                          <td colSpan={showFailedList ? 3 : 2} style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
                            No recipients found in this category.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

            </>
          )}
        </div>
        {error && <div className="mt-4 text-red-600">{error}</div>}
      </section>
    </div>
  );
}
